import { IClientType, IContactIntelligence } from '../Types/account';
import { IUserCondensed, IWhitelistUser } from '../Types/user';
import { IAccountCondensed } from '../Types/account';
import { ASSET_CATEGORY } from '../Types/assets';
/**
 * Sorts clients alphabetically by name (case-insensitive)
 */
export const sortClientsByName = (clients: IClientType[]): IClientType[] => {
  return [...clients].sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: "base" })
  );
};

export function getUserDisplayName(
  u: Pick<IUserCondensed, 'email' | 'username'> & { full_name?: string | null }
): string {
  return u.full_name || u.email || u.username || '';
}

/**
 * Sorts users alphabetically by full_name, falling back to email or username
 */
export const sortUsersByName = (users: IUserCondensed[]): IUserCondensed[] => {
  return [...users].sort((a, b) =>
    getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, {
      sensitivity: "base",
    })
  );
};

/**
 * Sorts whitelist users alphabetically by email
 */
export const sortWhitelistUsersByEmail = (users: IWhitelistUser[]): IWhitelistUser[] => {
  return [...users].sort((a, b) => 
    (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: "base" })
  );
};

/**
 * Sorts accounts alphabetically by name (case-insensitive)
 */
export const sortAccountsByName = (accounts: IAccountCondensed[]): IAccountCondensed[] => {
  return [...accounts].sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: "base" })
  );
};

/**
 * Comparison function for sorting accounts alphabetically by name (for use in sort callbacks)
 * Returns a number suitable for use in Array.sort() comparator
 */
export const compareAccountsByName = (a: IAccountCondensed, b: IAccountCondensed): number => {
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: "base" });
};

/**
 * Formats an asset category enum value to a display name
 */
const formatAssetCategoryName = (category: ASSET_CATEGORY): string => {
  return category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Sorts asset categories alphabetically by their formatted display name
 */
export const sortAssetCategories = (categories: ASSET_CATEGORY[]): ASSET_CATEGORY[] => {
  return [...categories].sort((a, b) => {
    const aName = formatAssetCategoryName(a);
    const bName = formatAssetCategoryName(b);
    return aName.localeCompare(bName, undefined, { sensitivity: "base" });
  });
};

/**
 * Comparator for contacts:
 * 1. Contacts with a user-defined index (drag-and-drop order) come first, sorted by index asc.
 * 2. Contacts without an index are sorted by overall_score desc.
 * 3. Contacts with a score always beat contacts with no score.
 * 4. Final tiebreaker: alphabetical by name.
 */
export const compareContacts = (a: IContactIntelligence, b: IContactIntelligence): number => {
  const indexA = a.insight_interaction_status.index;
  const indexB = b.insight_interaction_status.index;

  if (indexA !== null && indexB !== null) return indexA - indexB;
  if (indexA !== null && indexB === null) return -1;
  if (indexA === null && indexB !== null) return 1;

  const scoreA = a.overall_score;
  const scoreB = b.overall_score;
  if (scoreA != null && scoreB != null) {
    if (scoreA !== scoreB) return scoreB - scoreA;
  } else if (scoreA != null) {
    return -1;
  } else if (scoreB != null) {
    return 1;
  }

  return a.name.localeCompare(b.name);
};

/**
 * Returns a new sorted array of contacts using compareContacts ordering.
 */
export const sortContacts = (contacts: IContactIntelligence[]): IContactIntelligence[] => {
  return [...contacts].sort(compareContacts);
};

